import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { isRequired } from 'components/Questionnaires/validators';

export const QuestionJurisdictionDropDownField = ({ intl, blockId, question, className, onChange }) => {
  const [inputValue, setInputValue] = useState(question?.answer ?? question?.default ?? '');
  const jurisdictions = useSelector(state => state.app?.staticData?.jurisdictions ?? []);
  const treatyJurisdictions = useSelector(state => state.app?.staticData?.usTaxTreatyCountries ?? []);
  const annexIICountries = useSelector(state => state.app?.staticData?.annexIICountries ?? []);
  const locale = useSelector(state => state.app?.locale ?? 'en');
  const defaultLocale = 'en';

  const onInputChange = (event) => {
    setInputValue(event.target.value);
    onChange(question, event.target.value);
    return true;
  };

  const questionProps = () => {
    const props = Object.assign({}, question.componentProps);
    props.items = undefined; // Clear items so it doesn't display in html
    // eslint-disable-next-line react/prop-types
    delete props.preventTranslation; // Clear preventTranslation so it doesn't display in html
    // eslint-disable-next-line react/prop-types
    delete props.treatiesOnly; // Clear treatiesOnly so it doesn't display in html
    // eslint-disable-next-line react/prop-types
    delete props.annexIIOnly; // Clear annexIIOnly so it doesn't display in html
    return props;
  };

  useEffect(() => {
    if (inputValue) {
      onChange(question, inputValue);
    }
  }, []);

  useEffect(() => {
    if (inputValue !== question.answer) {
      setInputValue(question.answer ?? '');
    }
  }, [question.answer]);

  const formatJurisdictions = () => {
    const translatedJurisdictions = [];
    if (jurisdictions) {
      jurisdictions.filter((j) => {
        if (question.componentProps?.treatiesOnly === true && treatyJurisdictions) {
          return treatyJurisdictions.includes(j.isoJurisdictionName) || j?.aliases.some(a => treatyJurisdictions.includes(a)) ? j : undefined;
        }
        if (question.componentProps?.annexIIOnly === true && annexIICountries) {
          return annexIICountries.includes(j.isoJurisdictionName) ? j : undefined;
        }
        return j;
      }).forEach((j) => {
        const translated = j.locales;
        let dictionary = {};
        if (locale === defaultLocale) {
          dictionary = { key: j.countryCodeAlpha, value: j.isoJurisdictionName };
        } else {
          dictionary = { key: j.countryCodeAlpha, value: `${ translated[locale] } (${ j.isoJurisdictionName })` };
        }
        translatedJurisdictions.push(dictionary);
      });
    }

    return translatedJurisdictions;
  };

  const updateDropDownItems = () => {
    const newItems = [];
    if (jurisdictions && Object.keys(jurisdictions).length) {
      let defaultPlaceholder = question?.default;

      if (defaultPlaceholder) {
        defaultPlaceholder = intl.formatMessage({ id: question?.default });
      }
      newItems.push({ value: '', label: defaultPlaceholder ?? intl.formatMessage({ id: 'questionnaire.fields.questionJurisdictionDropDown.default' }) });

      const translatedJurisdictions = formatJurisdictions(jurisdictions);
      if (translatedJurisdictions) {
        translatedJurisdictions.map(jurisdiction => (
          newItems.push({ value: jurisdiction.key, label: jurisdiction.value })
        ));
      } else {
        // Should never hit this but here as backup
        jurisdictions.map(jurisdiction => (
          newItems.push({ value: jurisdiction.countryCodeAlpha, label: jurisdiction.isoJurisdictionName })));
      }
      return newItems;
    }
    return [];
  };

  const [dropdownItems, setDropDownItems] = useState(updateDropDownItems());

  useEffect(() => {
    const newItems = updateDropDownItems();
    setDropDownItems(newItems);
  }, [jurisdictions, locale, intl]);

  return (<select
    key={ `${ blockId }-${ question.id }` }
    id={ `${ blockId }-${ question.id }` }
    name={ question.id }
    value={ inputValue }
    onChange={ e => onInputChange(e) }
    autoComplete="no"
    className={ `${ className }${ question.isValid === false ? ' error' : '' }` }
    required={ isRequired(question) }
    { ...questionProps() }
    disabled={ question.disabled }
    aria-label={ (question.placeholder ?? question.label) ? intl.formatMessage({ id: question.placeholder ?? question.label }) : '' }
    aria-required={ isRequired(question) }
    aria-describedby={ question.isValid === false ? (`${ blockId }-${ question.id }-error`) : undefined }
  >
    { dropdownItems && dropdownItems.map(o => <option key={ o.value } value={ o.value }>{ o.label }</option>) }
  </select>);
};

QuestionJurisdictionDropDownField.displayName = 'QuestionJurisdictionDropDownField';

QuestionJurisdictionDropDownField.propTypes = {
  intl: PropTypes.shape().isRequired,
  blockId: PropTypes.string.isRequired,
  question: PropTypes.shape().isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

QuestionJurisdictionDropDownField.defaultProps = {
  className: '',
};

export default QuestionJurisdictionDropDownField;
